import capitalize from 'lodash/capitalize';
import React, { useState } from 'react';
import { Loading } from './Loading';
import { CognismConfiguration } from './CognismConfiguration';
import { CognismOverview } from './CognismOverview';
import { useCognismIsConnected } from './hooks/useCognismIsConnected';

const TABS = ['overview', 'configuration'] as const;
export type Tab = typeof TABS[number];
const ONLY_ADMIN_TABS: Tab[] = ['configuration'];

type Props = {
  tenant: number;
  hasAccessToConfiguration: boolean;
  initialTab: Tab;
  onActiveTabChange: (tab: Tab) => void;
};

export function CognismIntegration({
  tenant,
  onActiveTabChange,
  initialTab,
  hasAccessToConfiguration,
}: Props) {
  const { isLoading } = useCognismIsConnected(tenant);
  const availableTabs = hasAccessToConfiguration
    ? TABS
    : TABS.filter((tab) => !ONLY_ADMIN_TABS.includes(tab));

  const [activeTab, setActiveTab] = useState<Tab>(initialTab);

  function changeActiveTab(tab: Tab) {
    setActiveTab(tab);
    onActiveTabChange(tab);
  }

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-light bg-light border-top sticky-top">
        <ul className="nav nav-pills mx-auto">
          {availableTabs.map((tabName) => (
            <li
              className="nav-item tw-inline-flex tw-items-center"
              key={tabName}
            >
              <a
                className={`nav-link ${
                  activeTab === tabName ? 'active show' : ''
                }`}
                id={tabName}
                data-toggle="tab"
                href={`#${tabName}`}
                onClick={() => changeActiveTab(tabName)}
                role="tab"
              >
                {capitalize(tabName)}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {availableTabs.map((tabName, index) => (
        <div className="row ml-4" key={`tab_content_${index}`}>
          <div className="tab-content w-100" id="TabContent">
            <div
              key={`tab_content_${tabName}`}
              className={`tab-pane fade ${
                activeTab === tabName ? 'active show' : ''
              }`}
              id={`${tabName}-tab`}
              role="tabpanel"
            >
              <div className="row justify-content-center">
                <div className="col-8 mt-4 mb-4">
                  <div className="card box-shadow-3d mt-md-2 d-flex w-100">
                    <div className="card-header">
                      <div className="d-flex align-items-center">
                        <img
                          className="con-map-mk-integration-logo-size"
                          style={{ width: '30px' }}
                          src="/media/integrations/logos/cognism-icon.svg"
                        />
                        <h5 className="mb-0 ml-3">Cognism</h5>
                      </div>
                    </div>

                    <div className="card-body">
                      {isLoading ? (
                        <Loading loadingLabel="Checking Cognism connection" />
                      ) : (
                        <>
                          {tabName === 'overview' && (
                            <CognismOverview
                              tenant={tenant}
                              changeActiveTab={changeActiveTab}
                              hasAccessToConfiguration={
                                hasAccessToConfiguration
                              }
                            />
                          )}
                          {tabName === 'configuration' && (
                            <CognismConfiguration tenant={tenant} />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}
